import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@atlas/auth";

/**
 * Forgot password page
 * - Email form
 * - resetPassword on submit
 * - Navigate to reset-password on success
 */
export function ForgotPasswordPage(): React.ReactElement {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await resetPassword(email);
      // Navigate to reset password page with email for context
      navigate("/reset-password", { state: { email } });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to send reset code";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-center mb-1.5 text-text-primary">
          Reset your password
        </h1>
        <p className="text-center text-xs text-text-secondary mb-6">
          Enter your email address and we'll send you a code to reset your password.
        </p>

        {error && (
          <div className="alert-error mb-3">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="input-field"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary-lg w-full"
          >
            {isSubmitting ? "Sending..." : "Send Reset Code"}
          </button>
        </form>

        <p className="text-center mt-6 text-xs text-text-secondary">
          Remember your password?{" "}
          <Link to="/login" className="link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
